/**
 * Standard Mood & Vibe Categories for Lyricsverse
 */
export const MOOD_CATEGORIES = [
  { id: 'romantic', label: 'Romantic & Love', query: 'romantic', icon: 'Heart', color: 'pink' },
  { id: 'mass', label: 'Mass & High Energy', query: 'mass', icon: 'Flame', color: 'amber' },
  { id: 'sad', label: 'Sad & Melancholy', query: 'sad', icon: 'Frown', color: 'blue' },
  { id: 'dance', label: 'Dance & Party', query: 'dance', icon: 'PartyPopper', color: 'purple' },
  { id: 'soulful', label: 'Soulful & Devotional', query: 'soulful', icon: 'Sparkles', color: 'emerald' },
];

/**
 * Infer or predict song mood from title, lyrics, or existing tags.
 * Used as a fallback during future song lyrics generation workflows.
 */
export function inferSongMood(song = {}) {
  if (song.mood && typeof song.mood === 'string') {
    const cleanMood = song.mood.toLowerCase().trim();
    if (MOOD_CATEGORIES.some(m => m.id === cleanMood)) {
      return cleanMood;
    }
  }

  const textToAnalyze = [
    song.title || '',
    song.movieTitle || '',
    song.lyrics_english || '',
    song.lyrics_native || '',
    ...(Array.isArray(song.tags) ? song.tags : [])
  ].join(' ').toLowerCase();

  // Keyword Matching Heuristics
  if (/sad|pain|alone|tears|frown|broken|pyar|dardi|alvida|kannneer|kanneer|pirivu|dukha/i.test(textToAnalyze)) {
    return 'sad';
  }
  if (/party|dance|dj|club|kuthu|disco|thallumaala|nacho|groove|beat|shaker/i.test(textToAnalyze)) {
    return 'dance';
  }
  if (/mass|bgm|badass|fire|bullet|hero|beast|leo|power|furious|action|rowdy|vhukom/i.test(textToAnalyze)) {
    return 'mass';
  }
  if (/devotional|god|lord|prayer|bhajan|aarti|spiritual|soul|peace|shanti|temple|om/i.test(textToAnalyze)) {
    return 'soulful';
  }

  // Default fallback for love/romantic or general melodies
  return 'romantic';
}

/**
 * Resolves the primary song cover artwork using the exact logic from the lyrics page top section:
 * 1. YouTube Video Thumbnail (if video_url contains a valid 11-char YouTube ID)
 * 2. Movie Poster URL (song.moviePosterUrl or song.posterUrl)
 * 3. Cover Image / Thumbnail (song.cover_image_url / song.thumbnail_url / song.coverImageUrl)
 * 4. Fallback Image (/images/movies/fallback.svg)
 * 
 * @param {Object} song - The song object
 * @returns {string} The resolved cover image URL
 */
export function getSongCoverArtwork(song) {
  if (!song) return '/images/movies/fallback.svg';

  // 1. YouTube Video Thumbnail Priority
  const videoUrl = song.video_url || song.videoUrl;
  if (videoUrl && typeof videoUrl === 'string') {
    const regExp = /^.*(youtu\.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
    const match = videoUrl.match(regExp);
    if (match && match[2] && match[2].length === 11) {
      return `https://img.youtube.com/vi/${match[2]}/hqdefault.jpg`;
    }
  }

  // 2. Movie Poster Priority
  if (song.moviePosterUrl) return song.moviePosterUrl;
  if (song.posterUrl) return song.posterUrl;

  // 3. Song Cover Image / Thumbnail Priority
  if (song.cover_image_url) return song.cover_image_url;
  if (song.thumbnail_url) return song.thumbnail_url;
  if (song.coverImageUrl) return song.coverImageUrl;
  if (song.poster_url) return song.poster_url;
  if (song.poster_path) return song.poster_path;

  // 4. Default Fallback
  return '/images/movies/fallback.svg';
}
